import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Upload, FileText, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";

const LETTER_LABEL = {
  Applied:    { title: "Mark as Applied",   letter: "Submission Letter",  verb: "submit" },
  Approved:   { title: "Mark as Approved",  letter: "Approval Letter",    verb: "approve" },
  Rejected:   { title: "Mark as Rejected",  letter: "Rejection Letter",   verb: "reject" },
  Deferred:   { title: "Mark as Deferred",  letter: "Deferral Letter",    verb: "defer" },
  Withdrawn:  { title: "Mark as Withdrawn", letter: "Withdrawal Letter",  verb: "withdraw" },
};

/**
 * FilingStatusModal — opened when the officer picks a new Filing Status.
 * Collects: event date (<= today), the supporting letter (mandatory), and a
 * "I have notified the college" acknowledgement. On submit it uploads the
 * file, calls /filing-transition, and reloads the parent view.
 */
export default function FilingStatusModal({ open, onOpenChange, applicationId, newStatus, onDone }) {
  const cfg = LETTER_LABEL[newStatus] || { title: `Mark as ${newStatus}`, letter: "Supporting Letter", verb: "update" };
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [file, setFile] = useState(null);
  const [notified, setNotified] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const reset = () => {
    setDate(today); setFile(null); setNotified(false); setNotes("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = (v) => { if (!busy) { if (!v) reset(); onOpenChange(v); } };

  const submit = async () => {
    if (!date) return toast.error("Date is required");
    if (date > today) return toast.error("Date cannot be in the future");
    if (!file) return toast.error(`${cfg.letter} is required`);
    setBusy(true);
    try {
      // 1. Upload the letter — reuses the existing B2B upload endpoint.
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post(
        `/applications/${applicationId}/upload?doc_type=${encodeURIComponent(cfg.letter)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const letterId = up.data.id;
      const letterName = up.data.filename || file.name;
      // 2. Commit the transition.
      await api.post(`/applications/${applicationId}/filing-transition`, {
        status: newStatus,
        date,
        letter_file_id: letterId,
        letter_file_name: letterName,
        college_notified: notified,
        notes,
      });
      toast.success(`Filing status set to ${newStatus}`);
      if (!notified) {
        toast.warning("Reminder: send the letter to the college", { duration: 6000, icon: <Mail className="w-4 h-4" /> });
      }
      reset();
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save transition");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white max-w-lg" data-testid="filing-status-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500" />
            {cfg.title}
          </DialogTitle>
          <DialogDescription>
            Record the {cfg.letter.toLowerCase()} and the date on which the application was {cfg.verb}ed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Date<span className="text-rose-600">*</span></Label>
            <Input
              type="date"
              data-testid="filing-modal-date"
              max={today}
              value={date}
              onChange={e => setDate(e.target.value)}
            />
            <div className="text-[11px] text-slate-500">Cannot be in the future.</div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">{cfg.letter}<span className="text-rose-600">*</span></Label>
            <div className="flex items-center gap-2 min-w-0">
              <input
                ref={fileRef}
                type="file"
                data-testid="filing-modal-file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="h-9 shrink-0">
                <Upload className="w-4 h-4 mr-1.5" />{file ? "Change file" : "Choose file"}
              </Button>
              <div data-testid="filing-modal-filename" className="text-xs text-slate-600 truncate flex-1 min-w-0" title={file?.name}>
                {file ? file.name : <span className="text-slate-400">No file selected</span>}
              </div>
            </div>
            <div className="text-[11px] text-slate-500">PDF or image up to 15 MB.</div>
          </div>

          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <label className="flex items-start gap-2 cursor-pointer select-none text-sm text-amber-900">
              <input
                type="checkbox"
                data-testid="filing-modal-notified"
                checked={notified}
                onChange={e => setNotified(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">I have notified the college</span> about this {newStatus.toLowerCase()} status.
                <div className="text-[11px] text-amber-800/80 mt-0.5">
                  Leave unchecked to get a reminder to email the {cfg.letter.toLowerCase()} to the college.
                </div>
              </span>
            </label>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Notes (optional)</Label>
            <Textarea
              data-testid="filing-modal-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any extra context for the audit trail…"
              className="min-h-[70px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy}>Cancel</Button>
          <Button
            data-testid="filing-modal-submit"
            onClick={submit}
            disabled={busy || !date || !file}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {busy ? "Saving…" : `Save as ${newStatus}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
